import { ListContextProvider, useDeleteWithConfirmController, useListContext, useRefresh, useSaveContext } from "ra-core"
import { FC, SyntheticEvent, useCallback, useRef, useState } from "react"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, CardHeader, Divider, Box, IconButton, Tooltip, Button, Typography, alpha, useTheme } from "@mui/material"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { FunctionField, TextField } from "@/components/field"
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import useLocalListController from "@/components/list/useLocalListController"
import CreateOrEdit from "@/controller/CreateOrEdit"
import EditKey from "./EditTVKey"
import Confirm from "@/components/widgets/Confirm"
import DateField2 from "@/components/field/DateField2"
import { DataAccessPolicyResponseDto } from "@/gen/api/encrypt"
import { useSimpleSaveAction } from "@/controller/SaveContextProvider"
import Create from "@/controller/Create"


const dataAccessKeyListOptions : {sorters: Record<string, (a:DataAccessPolicyResponseDto & RecPatch, b:DataAccessPolicyResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
  }
}

const resource = 'token-validation-keys'

const TokenValidationKeys: FC<OdResourcePageProps> = (props) => {

  const listCtrl = useLocalListController({ resource, basePath: '', filter: { } }, dataAccessKeyListOptions)

  return (
    <ListContextProvider value={listCtrl}>
      <TokenValidationKeysBody />
    </ListContextProvider>
  )
}

const TokenValidationKeysBody: FC = () => {
  const listCtx = useListContext<DataAccessPolicyResponseDto & RecPatch>()
  
  const [states, setStates] = useState({
    openEdit: false,
    recordToEdit: null as DataAccessPolicyResponseDto | null,
    keyToRegenerate: null as DataAccessPolicyResponseDto | null,
  })

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const savedHandlers = useDefaultSavedHandlers(() => `${states.recordToEdit ? 'Edit' : 'Create'} Token Validation Key`)

  const recordToDel = useRef<DataAccessPolicyResponseDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Token Validation Key')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    resource,
    record: recordToDel.current as any,
    ...delHandlers,
  })

  const theme = useTheme()
  const rowStyles = (d: DataAccessPolicyResponseDto&RecPatch) => {
    const bgcolor = 
      d.isManaged ? alpha(theme.palette.warning.light, 0.1) : null
    return bgcolor ? { backgroundColor: bgcolor } : {}
  }
  const regenTVKHandlers = useDefaultSavedHandlers(`Regenerate managed Organization Token Validation Key`)

  return (
    <>
      <Card>
        <CardHeader 
          title={
            <>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Token Validation Keys
                <Tooltip title="Token Validation Keys will be used to validate user tokens from applications.">
                  <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Tooltip>
              </Box>
            </>
          }
          action={
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              startIcon={<AddIcon />}
              onClick={() => setStates(v => ({...v, openEdit: true, recordToEdit: null}))}
            >
              New Key
            </Button>
          }
        />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& .description': { maxWidth: 750, display: 'inline-block', wordBreak: 'break-word' },
              }}
              rowStyle={rowStyles}
              {...listCtx}
            >
              <DateField2 source="createdTime" label="Created Time" sortable={false} showTime />
              <TextField source="description" label="Description" sortable={false} className="description" />
              <FunctionField 
                label="Action" 
                sortable={false} 
                render={(d?:DataAccessPolicyResponseDto&RecPatch) => {
                  if (!d) { return null }
                  return (
                    <>
                      {
                        d.isManaged
                        ? <Tooltip title="Regenerate">
                            <IconButton
                              sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                              onClick={(e) => { 
                                setStates(v => ({...v, keyToRegenerate: d}))
                                e.stopPropagation()
                              }}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        : <Tooltip title="Delete">
                            <IconButton
                              sx={{
                                color: 'text.primary' as any,
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                              onClick={(e:any) => {
                                recordToDel.current = d as any
                                handleDialogOpen(e)
                              }}
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>}
                      <Tooltip title="View">
                        <IconButton
                          sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                          onClick={(e) => {
                            setStates(v => ({
                              ...v,
                              recordToEdit: d,
                              openEdit: true,
                            }))
                          }}
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )
                }}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <CreateOrEdit resource={resource} basePath="" record={states.recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
        {states.openEdit && 
          <EditKey 
            disabled={states.recordToEdit?.isManaged} 
            open={states.openEdit} 
            setOpen={(x:boolean) => {setStates(v => ({...v, openEdit: x})); if (!x) { setTimeout(() => setStates(v => ({...v, recordToEdit:null}))) }}} 
          />}
      </CreateOrEdit>
      <Confirm
        isOpen={openConfirm}
        title={"Delete Token Validation Key"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={(e:any) => {handleDialogClose(e); recordToDel.current = null}}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(recordToDel.current?.name) ? ` "${recordToDel.current?.name}" ` : " "}
                Token Validation Key.
              </Typography>
            </Box>
          </Box>
        )}
      />
      <Create resource="token-validation-key-regenerate" basePath="" {...regenTVKHandlers}>
        <ConfirmRegenerate d={states.keyToRegenerate} onClose={() => setStates(v => ({...v, keyToRegenerate: null}))} />
      </Create>
    </>
  )
}

export default TokenValidationKeys;


const ConfirmRegenerate : FC<{d:DataAccessPolicyResponseDto|null, onClose:()=>void}> = ({
  d,
  onClose,
}) => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  
  const sendRegenerate = (e:SyntheticEvent) => {
    d && save(d.databaseId as any, { onSuccess: onClose })
  }

  return (
    <Confirm
      isOpen={!!d}
      loading={saving}
      title={"Regenerate managed Organization Token Validation Key"}
      onConfirm={sendRegenerate}
      onClose={onClose}
      confirm="Confirm"
      cancel="Cancel"
      content={(
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Regenerate this key would invalidate all Data Access Token previously signed by the key. 
              Do you wish to regenerate it?
            </Typography>
          </Box>
        </Box>
      )}
    />
  )
}

